import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMExportOutput,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {ReactNode} from 'react';

import styles from './EnhancedHeadingNode.module.css';

export type SerializedBlockedEditNode = Spread<
  {
    content: string;
    className?: string;
  },
  SerializedLexicalNode
>;

export class BlockedEditNode extends DecoratorNode<ReactNode> {
  __content: string;
  __className?: string;

  static getType(): string {
    return 'BlockedEdit';
  }

  static clone(node: BlockedEditNode): BlockedEditNode {
    return new BlockedEditNode(node.__content, node.__className, node.__key);
  }

  constructor(content: string, className?: string, key?: NodeKey) {
    super(key);
    this.__content = content;
    this.__className = className;
  }

  createDOM(): HTMLElement {
    const element = document.createElement('div');
    element.setAttribute('data-lexical-blocked-edit', 'true');
    element.classList.add(styles.blockedEditNode);

    if (this.__className) {
      element.classList.add(this.__className);
    }

    return element;
  }

  updateDOM(): boolean {
    return false;
  }

  exportDOM(): DOMExportOutput {
    // Return null to exclude from copy/paste operations
    return {element: null};
  }

  static importDOM(): null {
    // Don't import blocked edit nodes from external content
    return null;
  }

  static importJSON(serializedNode: SerializedBlockedEditNode): BlockedEditNode {
    const {content, className} = serializedNode;
    return $createBlockedEditNode(content, className);
  }

  exportJSON(): SerializedBlockedEditNode {
    return {
      content: this.__content,
      className: this.__className,
      type: 'BlockedEdit',
      version: 1,
    };
  }

  getTextContent(): string {
    // Return empty string so it doesn't affect text content
    return '';
  }

  getContent(): string {
    return this.__content;
  }

  getClassName(): string | undefined {
    return this.__className;
  }

  setContent(content: string): void {
    const writable = this.getWritable();
    writable.__content = content;
  }

  setClassName(className: string | undefined): void {
    const writable = this.getWritable();
    writable.__className = className;
  }

  component(): ReactNode {
    return null;
  }

  decorate(): ReactNode {
    return (
      <div className={styles.blockedEditContent}>
        <div className={styles.blockedEditText}>{this.__content}</div>
      </div>
    );
  }
}

export function $createBlockedEditNode(content: string, className?: string): BlockedEditNode {
  return $applyNodeReplacement(new BlockedEditNode(content, className));
}

export function $isBlockedEditNode(node: unknown): node is BlockedEditNode {
  return node instanceof BlockedEditNode;
}
